import { useEffect, useMemo } from 'react';
import { AggregationContainerForApi } from './containers/aggregation/aggregation-container';
import { GroupByContainerForApi } from './containers/group-by/group-by-container';
import { TargetContainerForApi } from './containers/target/target-container';
import { ChartContainerForApi } from './containers/chart/chart-container';
import { DistributionContainerForApi } from './containers/distribution/distribution-container';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { 
  selectAggregateFieldState, 
  selectGroupByFieldState, 
  selectTargetFieldState, 
  selectDistributionFieldState, 
  selectChartState
} from 'app/area/reports/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { AddInstance, ClearInstance } from 'app/area/reports/action-types';
import { selectFavouriteId } from 'app/area/favourite/selectors';
import { AppDispatch } from 'app/infrastructure/configure-store';

export function useInstanceSlice() {
  const instanceKey = useInstanceKey();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    dispatch(new AddInstance(instanceKey));
    return () => {
      dispatch(new ClearInstance(instanceKey));
    };
  }, [instanceKey]);
}

export function useContainerComponents(api: ReportCommonApi) {
  const {
    reportApiOverrides
  } = useInstanceOptions();

  const finalApi = useMemo(() => ({
    ...api,
    ...(reportApiOverrides || {})
  }), [api, reportApiOverrides]);

  return useMemo(() => ({
    AggregationContainer: AggregationContainerForApi(finalApi),
    GroupByContainer: GroupByContainerForApi(finalApi),
    TargetContainer: TargetContainerForApi(finalApi),
    DistributionContainer: DistributionContainerForApi(finalApi),
    ChartContainer: ChartContainerForApi(finalApi)
  }), [finalApi]);
}

export function useSelectors() {
  const instanceKey = useInstanceKey();
  const aggregateFieldState = useSelector(selectAggregateFieldState(instanceKey));
  const groupByFieldState = useSelector(selectGroupByFieldState(instanceKey));
  const targetFieldState = useSelector(selectTargetFieldState(instanceKey));
  const distributionFieldState = useSelector(selectDistributionFieldState(instanceKey));
  const chartState = useSelector(selectChartState(instanceKey));

  return {
    aggregateFieldState,
    groupByFieldState,
    targetFieldState,
    distributionFieldState,
    chartState
  };
}

export function useWorkingDefinitionFavouriteId() {
  const instanceKey = useInstanceKey();
  return useSelector(selectFavouriteId(instanceKey));
}
